<script setup>
import { computed, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const store = useStore();

const [showDropdown, toggleDropdown] = useToggle(false);

const currentChat = useMapGetter('getSelectedChat');
const getPendingFollowUps = useMapGetter('followUps/getPendingFollowUps');

const conversationId = computed(() => currentChat.value?.id);

const pendingFollowUps = computed(() =>
  conversationId.value ? getPendingFollowUps.value(conversationId.value) : []
);

const pendingCount = computed(() => pendingFollowUps.value.length);

watch(
  conversationId,
  newId => {
    if (newId) {
      // Fetch all statuses (not just pending) into the shared store so resolved
      // follow-up note bubbles can still resolve their status; the badge/list
      // below narrow to pending via the getter.
      store.dispatch('followUps/get', { conversationId: newId });
    }
  },
  { immediate: true }
);

const scheduledTime = followUp =>
  messageTimestamp(followUp.scheduled_at, 'LLL d, h:mm a');

const markDone = followUp => {
  store.dispatch('followUps/complete', {
    conversationId: conversationId.value,
    id: followUp.id,
  });
};

const cancel = followUp => {
  store.dispatch('followUps/cancel', {
    conversationId: conversationId.value,
    id: followUp.id,
  });
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="pendingCount"
    v-on-clickaway="() => toggleDropdown(false)"
    class="relative flex items-center group"
  >
    <NextButton
      v-tooltip="$t('CONVERSATION.FOLLOW_UP.LIST_TITLE')"
      sm
      ghost
      slate
      icon="i-ph-alarm"
      :label="`${pendingCount}`"
      class="rounded-md group-hover:bg-n-alpha-2"
      @click="toggleDropdown()"
    />
    <div
      v-if="showDropdown"
      class="absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0 w-80 max-h-96 overflow-y-auto bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container rounded-xl shadow-lg p-1"
    >
      <div class="px-2 py-1.5 text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION.FOLLOW_UP.LIST_TITLE') }}
      </div>
      <div
        v-for="followUp in pendingFollowUps"
        :key="followUp.id"
        class="flex flex-col gap-1 px-2 py-2 rounded-md hover:bg-n-alpha-1"
      >
        <p class="text-sm text-n-slate-12 line-clamp-2 m-0">
          {{ followUp.description }}
        </p>
        <div class="flex items-center justify-between gap-2">
          <span class="flex items-center gap-1 text-xs text-n-slate-11">
            <Icon icon="i-ph-alarm" class="size-3" />
            {{
              $t('CONVERSATION.FOLLOW_UP.SCHEDULED_FOR', {
                time: scheduledTime(followUp),
              })
            }}
          </span>
          <div class="flex items-center gap-1">
            <NextButton
              xs
              ghost
              teal
              :label="$t('CONVERSATION.FOLLOW_UP.MARK_DONE')"
              @click="markDone(followUp)"
            />
            <NextButton
              xs
              ghost
              ruby
              :label="$t('CONVERSATION.FOLLOW_UP.CANCEL')"
              @click="cancel(followUp)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
